import { useCallback, useEffect, useMemo, useState } from "react";
import { DisclosureOrderTitles, Disclosures } from "types";

const useDisclosureOrderTitles = (disclosures: Disclosures) => {
  const [disclosureOrderTitles, setDisclosureOrderTitles] = useState<DisclosureOrderTitles>([]);

  const disclosuresToTitleMap = useMemo(() => {
    return (
      disclosures?.data?.map((disclosure) => {
        return disclosure.attributes.title;
      }) ?? []
    );
  }, [disclosures]);

  const recursivelyOrderDisclosureByTitle = useCallback((data: Array<string>, index: number = 1) => {
    const disclosures = Array.from(document.querySelectorAll("[data-disclosure-title]"));

    const disclosureTitles = disclosures.map((disclosure) => {
      return disclosure.getAttribute("data-disclosure-title");
    });

    const uniqueDisclosureTitles = Array.from(new Set(disclosureTitles));

    disclosures.forEach((disclosure) => {
      const getIndex = (disclosureTitle: string | null) => {
        return uniqueDisclosureTitles.findIndex((title) => title === disclosureTitle) + 1;
      };

      disclosure.innerHTML = `${getIndex(disclosure.getAttribute("data-disclosure-title"))}`;
    });

    const isEveryDisclosureFound = data.every((title) =>
      uniqueDisclosureTitles.some((uniqueTitle) => uniqueTitle?.toLowerCase() === title.toLowerCase())
    );

    // If we found all the disclosures, then we can set the order of the disclosures
    // Or if we have called recursivelyOrderDisclosureByTitle more than 100 times, then we should just set the order of the disclosures
    if (isEveryDisclosureFound || index > 100) {
      return setDisclosureOrderTitles(uniqueDisclosureTitles);
    }

    // Wait for the next render to call recursivelyOrderDisclosureByTitle again
    // By not providing a second argument to setTimeout, we are using the default of waiting for the next render
    setTimeout(() => {
      recursivelyOrderDisclosureByTitle(data, index + 1);
    });
  }, []);

  useEffect(() => {
    recursivelyOrderDisclosureByTitle(disclosuresToTitleMap);
  }, [disclosuresToTitleMap, recursivelyOrderDisclosureByTitle]);

  return { disclosureOrderTitles };
};

export { useDisclosureOrderTitles };
